import { getCookieValueByKey, setCookie, uuidService } from '@rbx/core';
import { TProduct } from '../core/types';
import getSessionCookieDomain from './getSessionCookieDomain';

type TSessionCache = { sessionId: string; lastActivity: number; timestamp: number };
type TProductSessionCookie = Record<TProduct, { sessionId: string; lastActivity: number }>;

const INACTIVE_SESSION_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds
const CACHE_EXPIRY_TIME = 5 * 1000; // 5 seconds in milliseconds
const COOKIE_MAX_AGE = 86400; // 24 hours in seconds

type TSessionService = {
  productName: TProduct;
  cacheExpiryTime?: number;
  inactiveSessionExpireTime?: number;
  onSessionRefresh?: (sessionId: string) => void;
};
export interface ISessionService {
  getOrCreateSessionId: () => string | undefined;
}

export default class SessionService implements ISessionService {
  productName: TProduct;

  cache: TSessionCache | undefined;

  cacheExpiryTime: number;

  inactiveSessionExpireTime: number;

  onSessionRefresh: (sessionId: string) => void;

  constructor({
    productName,
    cacheExpiryTime = CACHE_EXPIRY_TIME,
    inactiveSessionExpireTime = INACTIVE_SESSION_EXPIRY_TIME,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSessionRefresh = () => {},
  }: TSessionService) {
    this.productName = productName;
    this.cacheExpiryTime = cacheExpiryTime;
    this.inactiveSessionExpireTime = inactiveSessionExpireTime;
    this.onSessionRefresh = onSessionRefresh;
  }

  // Session Cookie Format:
  //  - "UnifiedLoggerSession={productName1}={sessionId: 'sessionId1', lastActivity: 'timestamp1'}&{productName2}={sessionId: 'sessionId2', lastActivity: 'timestamp2'}"
  static parseCookieValue(): TProductSessionCookie {
    const cookie = getCookieValueByKey('UnifiedLoggerSession');
    if (!cookie) {
      return {} as TProductSessionCookie;
    }

    const products = decodeURIComponent(cookie)
      .split('&')
      .reduce((acc, cur) => {
        const [productNameKey, productData] = cur.split('=');
        const productName = productNameKey as TProduct;
        acc[productName] = JSON.parse(productData);
        return acc;
      }, {} as TProductSessionCookie);

    return products;
  }

  initCacheFromCookies(): TSessionCache | undefined {
    const productSessionData = SessionService.parseCookieValue();
    if (productSessionData[this.productName] === undefined) {
      return undefined;
    }

    const cache = {
      ...productSessionData[this.productName],
      timestamp: Date.now(),
    } as TSessionCache;
    return cache;
  }

  /**
   * Get or create a session ID for the current product.
   * Session info is stored in the cookie, and cached in memory for 5 seconds.
   *
   * 1. If the session is cached, it will be used if it's active. If it's not active, it will
   *    be refreshed based on the latest cookie.
   *
   * 2. If the session is not cached, sync the latest session info from the cookie. If it's not active,
   *    refresh the session. Otherwise, update the lastActivity to the current time in the cache and cookie.
   */
  getOrCreateSessionId(): string {
    try {
      const now = Date.now();
      if (this.isSessionCached(now)) {
        if (this.isSessionActive(now)) {
          // If the session is active and cached, simply update the lastActivity without updating the cookie.
          this.cache!.lastActivity = now; // cache always exists here.
          return this.cache!.sessionId;
        }
        return this.refreshSessionIdIfNeeded(now);
      }
      this.cache = this.initCacheFromCookies();

      if (!this.isSessionActive(now)) {
        return this.refreshSessionId(now);
      }
      this.cache!.lastActivity = now;
      this.updateCookie();
      return this.cache!.sessionId;
    } catch (error) {
      console.error('fail to get session_id');
      return '';
    }
  }

  private isSessionActive(now: number): boolean {
    if (!this.cache) {
      return false; // Considered not active if cache doesn't exist
    }
    return now - this.cache.lastActivity < this.inactiveSessionExpireTime;
  }

  private isSessionCached(now: number): boolean {
    if (!this.cache) {
      return false;
    }
    return now - this.cache.timestamp < this.cacheExpiryTime;
  }

  refreshSessionId(now: number): string {
    const newSessionId = uuidService.generateRandomUuid();
    const lastActivity = Date.now();
    this.cache = { sessionId: newSessionId, lastActivity, timestamp: now };
    this.onSessionRefresh(newSessionId);
    this.updateCookie();
    return newSessionId;
  }

  refreshSessionIdIfNeeded(now: number): string {
    // Read the latest session info from the cookie to see if it's already refreshed.
    const product = this.initCacheFromCookies();
    if (!product) {
      return this.refreshSessionId(now);
    }
    const { sessionId, lastActivity } = product;
    if (now - lastActivity > this.inactiveSessionExpireTime) {
      return this.refreshSessionId(now);
    }
    this.cache = { sessionId, lastActivity: now, timestamp: now };
    this.updateCookie(); // update lastActivity of refreshed session
    return sessionId;
  }

  updateCookie() {
    if (!this.cache) {
      return;
    }
    const products = SessionService.parseCookieValue();
    products[this.productName] = this.cache;

    const updatedProducts = Object.entries(products)
      .map(
        ([key, { sessionId, lastActivity }]) =>
          `${key}=${JSON.stringify({ sessionId, lastActivity })}`
      )
      .join('&');

    setCookie('UnifiedLoggerSession', updatedProducts, {
      domain: getSessionCookieDomain(),
      path: '/',
      'max-age': COOKIE_MAX_AGE,
    });
  }
}
